import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import {
	pickAiSenderOption,
	preferThreadSender,
	type AiSender,
} from "./resolveAiSenderUserId.util";

import type { Draft, DraftSender } from "./inboxDraft.types";
import type { User } from "@/pages/Public/pages/Login/schema/login";

interface ApplyAiSenderParams {
	drafts: Draft[];
	senderOptions: SenderRecipientOption[];
	aiSender: AiSender;
	/** The thread's own sender option, when the lead has a conversation (resolveInboxChannel). */
	threadSender?: SenderRecipientOption;
	user: User | null | undefined;
}

/**
 * Sentinel label written by the AI draft-build fallback (see buildFilteredAiDrafts)
 * when no sender option matched. A resolved or user-edited draft always carries a
 * real option label (e.g. "Me (LinkedIn — will send invite)"), so this string
 * uniquely marks a sender that still needs resolving.
 */
const AI_SENDER_PLACEHOLDER_LABEL = "Sender";

const isAiSenderPlaceholder = (from: DraftSender | undefined): boolean =>
	from?.label === AI_SENDER_PLACEHOLDER_LABEL;

/**
 * Re-resolve the AI generation sender for drafts whose `from` is still the
 * unresolved "Sender" placeholder.
 *
 * The placeholder is written when the persisted AI sequence is turned into drafts
 * *before* the async LinkedIn connection check completes: at that moment
 * `buildSenderOptions` has emitted no LinkedIn sender yet, so `pickAiSenderOption`
 * can't match the AI-chosen sender and the draft falls back to the placeholder
 * (PREC-3674 — sender empty in the Lead Panel). Once the check completes and the
 * LinkedIn options appear, this fills those placeholders with the AI sender.
 *
 * The thread's own mailbox wins over another mailbox of the same sender, and a draft without a
 * generation sender takes the thread's sender (`preferThreadSender`, PREC-4312 E4): on a first
 * page load every restored draft is a placeholder, so this is where the thread's mailbox is kept.
 *
 * Only placeholder drafts are touched — resolved or user-edited senders keep their
 * value. Returns the same array reference when nothing changes so React's bailout
 * keeps the render stable.
 */
export const applyAiSenderToPlaceholderDrafts = ({
	drafts,
	senderOptions,
	aiSender,
	threadSender,
	user,
}: ApplyAiSenderParams): Draft[] => {
	if (!aiSender?.userId && !threadSender) {
		return drafts;
	}

	let changed = false;

	const next = drafts.map((draft) => {
		if (!isAiSenderPlaceholder(draft.from)) {
			return draft;
		}

		const thread = threadSender?.type === draft.from.type ? threadSender : undefined;
		const picked =
			preferThreadSender(pickAiSenderOption(senderOptions, draft.from.type, aiSender), thread) ??
			thread;

		if (!picked) {
			return draft;
		}

		changed = true;

		return {
			...draft,
			from: {
				userId: picked.userId ?? user?.id ?? 0,
				type: draft.from.type,
				label: picked.label,
				emailId: picked.emailId,
				emailAddress: picked.emailAddress,
			},
		};
	});

	return changed ? next : drafts;
};
